import { APICore } from "./apiCore";

const api = new APICore();

/** Lista relatórios do paciente (rascunhos e publicados) */
const listarPorPaciente = (pacienteId: string | number) => {
  return api.get(`/relatorio-parcial/paciente/${pacienteId}`);
};

/** Lista apenas relatórios publicados (histórico) */
const listarHistorico = (pacienteId: string | number) => {
  return api.get(`/relatorio-parcial/paciente/${pacienteId}/historico`);
};

export type PeriodicidadeValor = "mensal" | "bimestral" | "trimestral" | "quadrimestral" | "semestral" | "anual";

/** Cria rascunho e uma sessão por especialidade do paciente. */
const criarRascunho = (
  pacienteId: string | number,
  body?: {
    referente_ao_periodo?: string | null;
    periodicidade?: PeriodicidadeValor;
    periodicidade_em_dias?: number;
    data_relatorio? : string
  }
) => {
  return api.create(`/relatorio-parcial/paciente/${pacienteId}`, body ?? {});
};

/** Atualiza o rascunho (ex.: referente_ao_periodo/periodicidade/avaliacao_reavalicao). Só rascunho (não publicado). PATCH. */
const atualizarRelatorio = (
  id: string | number,
  data: {
    referente_ao_periodo?: string | null;
    periodicidade?: PeriodicidadeValor;
    periodicidade_em_dias?: number;
    data_relatorio? : string
    avaliacao_reavalicao?: string | null;
  }
) => {
  return api.updatePatch(`/relatorio-parcial/${id}`, data);
};

/** Atualiza somente avaliacao_reavalicao. PUT dedicado. Rascunho ou publicado. */
const atualizarAvaliacaoReavalicao = (
  id: string | number,
  avaliacao_reavalicao: string | null
) => {
  return api.update(`/relatorio-parcial/${id}/avaliacao-reavalicao`, {
    avaliacao_reavalicao,
  });
};

/** Atualiza somente a periodicidade. PUT dedicado. Rascunho ou publicado; também aplica aos rascunhos pendentes do paciente. */
const atualizarPeriodicidade = (
  id: string | number,
  periodicidade: PeriodicidadeValor
) => {
  return api.update(`/relatorio-parcial/${id}/periodicidade`, { periodicidade });
};

/** Detalhe do relatório (sessões + respostas do checklist) */
const obterPorId = (id: string | number) => {
  return api.get(`/relatorio-parcial/${id}`);
};

/** Atualiza somente o conteúdo textual da sessão daquela especialidade */
const atualizarSessao = (
  relatorioId: string | number,
  especialidadeId: string | number,
  data: { conteudo: string; imagens?: string[] }
) => {
  return api.update(
    `/relatorio-parcial/${relatorioId}/sessao/${especialidadeId}`,
    data
  );
};

/** Atualiza somente o colaborador atribuído (ponto focal) da sessão. PUT dedicado /atribuido */
const atualizarAtribuidoSessao = (
  relatorioId: string | number,
  especialidadeId: string | number,
  atribuido_a_colaborador_id: number | null
) => {
  return api.update(
    `/relatorio-parcial/${relatorioId}/sessao/${especialidadeId}/atribuido`,
    { atribuido_a_colaborador_id }
  );
};

const atualizarAplicadorAdicionalSessao = (
  relatorioId: string | number,
  especialidadeId: string | number,
  aplicador_adicional_id: number | null
) => {
  return api.update(
    `/relatorio-parcial/${relatorioId}/sessao/${especialidadeId}/atribuido`,
    { aplicador_adicional_id }
  );
};


/** Salva as respostas do checklist */
const salvarChecklist = (
  relatorioId: string | number,
  data: {
    respostas: Array<{
      checklist_relatorio_parcial_id: number;
      checklist_relatorio_parcial_opcao_id: number | null;
      valor_texto: string | null;
    }>;
  }
) => {
  return api.update(`/relatorio-parcial/${relatorioId}/checklist`, data);
};

/** Publica o relatório. Opcionalmente envia especialidade_ids, avaliacao_reavalicao e data_relatorio. */
const publicar = (
  id: string | number,
  body?: { especialidade_ids?: number[]; avaliacao_reavalicao?: string | null; data_relatorio?: string | null }
) => {
  return api.create(`/relatorio-parcial/${id}/publicar`, body ?? {});
};

/** Retorna URL/dados do PDF para download */
const obterPdf = (id: string | number) => {
  return api.get(`/relatorio-parcial/${id}/pdf`);
};

/** Regenera o PDF do relatório parcial já publicado. Body opcional: { especialidade_ids?: number[] }. */
const regenerarPdf = (id: string | number, body?: { especialidade_ids?: number[] }) => {
  return api.create(`/relatorio-parcial/${id}/regenerar-pdf`, body ?? {});
};

/** Parâmetros de paginação e filtros da listagem de gerenciamento */
export type ListagemGerenciamentoParams = {
  currentPage?: number;
  pageSize?: number;
  q?: string;
  status?: string;
  periodicidade?: string;
};

/** Exclui um relatório publicado (requer permissão 155 ou acesso total 153). Soft delete. */
const deletarRelatorio = (id: string | number) =>
  api.delete(`/relatorio-parcial/${id}`);

/** Visão anual macro: grade de meses por paciente para planejamento. */
const visaoAnual = (ano: number) =>
  api.get(`/relatorio-parcial/visao-anual?ano=${ano}`);

/** Listagem de gerenciamento de relatórios parciais da unidade (resposta sempre paginada) */
const listagemGerenciamento = (params?: ListagemGerenciamentoParams) => {
  const search = new URLSearchParams();
  if (params?.currentPage != null) search.set("currentPage", String(params.currentPage));
  if (params?.pageSize != null) search.set("pageSize", String(params.pageSize));
  if (params?.q != null && String(params.q).trim() !== "") search.set("q", String(params.q).trim());
  if (params?.status != null && String(params.status).trim() !== "") search.set("status", String(params.status).trim());
  if (params?.periodicidade != null && String(params.periodicidade).trim() !== "") search.set("periodicidade", String(params.periodicidade).trim());
  const qs = search.toString();
  return api.get(`/relatorio-parcial/listagem${qs ? `?${qs}` : ""}`);
};

export {
  listarPorPaciente,
  listarHistorico,
  criarRascunho,
  atualizarRelatorio,
  atualizarAvaliacaoReavalicao,
  atualizarPeriodicidade,
  obterPorId,
  atualizarSessao,
  atualizarAtribuidoSessao,
  atualizarAplicadorAdicionalSessao,
  salvarChecklist,
  publicar,
  obterPdf,
  regenerarPdf,
  deletarRelatorio,
  visaoAnual,
  listagemGerenciamento,
};
